import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import InventoryProductPicker from './InventoryProductPicker';

const emptyRow = () => ({
  variantId: "",
  adjustmentQty: "",
  unitCost: "",
});

const reasons = [
  {
    value: "DAMAGED_STOCK",
    label: "Damaged Stock",
    direction: "OUT",
  },
  {
    value: "LOST_STOCK",
    label: "Lost Stock",
    direction: "OUT",
  },
  {
    value: "FOUND_STOCK",
    label: "Found Stock",
    direction: "IN",
  },
  {
    value: "OTHER_IN",
    label: "Other Stock In",
    direction: "IN",
  },
  {
    value: "OTHER_OUT",
    label: "Other Stock Out",
    direction: "OUT",
  },
];

const number = (value) => Number(value || 0);

const quantity = (value) =>
  number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });

const today = () => new Date().toISOString().slice(0, 10);

const variantLabel = (balance) => {
  const parts = [
    balance.variant?.product?.name,
    balance.variant?.color?.name,
    balance.variant?.size?.name,
    balance.variant?.sku,
  ].filter(Boolean);

  return parts.join(" · ");
};

export default function StockAdjustmentPage() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [balances, setBalances] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [reason, setReason] = useState("DAMAGED_STOCK");
  const [occurredAt, setOccurredAt] = useState(today());
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadBalances = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const response = await apiRequest("/inventory/balances", {
        accessToken,
      });

      setBalances(response || []);
    } catch (err) {
      setError(err.message || "Unable to load inventory balances.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadBalances();
  }, [isAuthLoading, accessToken, loadBalances]);

  const stores = useMemo(() => {
    const uniqueStores = new Map();

    balances.forEach((balance) => {
      if (balance.store) {
        uniqueStores.set(balance.store.id, balance.store);
      }
    });

    return [...uniqueStores.values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [balances]);

  const storeBalances = useMemo(
    () => balances.filter((balance) => balance.storeId === storeId),
    [balances, storeId],
  );

  const balanceByVariantId = useMemo(
    () =>
      new Map(
        storeBalances.map((balance) => [balance.variantId, balance]),
      ),
    [storeBalances],
  );

  const selectedReason = reasons.find((item) => item.value === reason);
  const isStockIn = selectedReason?.direction === "IN";

  const addRow = () => {
    setRows((previous) => [...previous, emptyRow()]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) {
      setError("At least one adjustment row is required.");
      return;
    }

    setRows((previous) =>
      previous.filter((_, rowIndex) => rowIndex !== index),
    );
  };

  const updateRow = (index, field, value) => {
    setRows((previous) =>
      previous.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const changeStore = (value) => {
    setStoreId(value);
    setRows([emptyRow()]);
    setError("");
    setSuccess("");
  };

  const submitAdjustment = async () => {
    setError("");
    setSuccess("");

    if (!storeId) {
      setError("Select a store or warehouse.");
      return;
    }

    const selectedVariantIds = rows
      .map((row) => row.variantId)
      .filter(Boolean);

    if (selectedVariantIds.length !== rows.length) {
      setError("Select a product variant in every row.");
      return;
    }

    if (new Set(selectedVariantIds).size !== selectedVariantIds.length) {
      setError("The same variant cannot be adjusted more than once.");
      return;
    }

    if (
      rows.some(
        (row) =>
          !row.adjustmentQty ||
          number(row.adjustmentQty) <= 0,
      )
    ) {
      setError("Enter an adjustment quantity greater than zero in every row.");
      return;
    }

    if (
      !isStockIn &&
      rows.some((row) => {
        const balance = balanceByVariantId.get(row.variantId);
        const currentStock = number(balance?.quantityOnHand);
        return number(row.adjustmentQty) > currentStock;
      })
    ) {
      setError(
        "An outward adjustment cannot exceed current stock for products that do not allow negative inventory.",
      );
      return;
    }

    try {
      setSaving(true);

      const reasonLabel = selectedReason?.label || "Stock Adjustment";

      const response = await apiRequest("/inventory/adjustments/bulk", {
        method: "POST",
        accessToken,
        body: {
          storeId,
          occurredAt,
          note: `[${reasonLabel}]${remarks.trim() ? ` ${remarks.trim()}` : ""}`,
          items: rows.map((row) => ({
            variantId: row.variantId,
            quantityChange: isStockIn
              ? number(row.adjustmentQty)
              : -number(row.adjustmentQty),
            unitCost:
              row.unitCost === ""
                ? undefined
                : number(row.unitCost),
          })),
        },
      });

      setSuccess(
        `Adjustment submitted successfully. Reference: ${response.adjustmentReferenceId}`,
      );

      setRows([emptyRow()]);
      setRemarks("");

      await loadBalances();
    } catch (err) {
      setError(err.message || "Unable to submit stock adjustment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {pickerOpen && <InventoryProductPicker open onClose={() => setPickerOpen(false)} storeId={storeId} balances={balances} accessToken={accessToken} onAdd={selected => setRows(current => {
        const existing = current.filter(row => row.variantId);
        const ids = new Set(existing.map(row => row.variantId));
        return [...existing, ...selected.filter(row => !ids.has(row.variantId)).map(row => ({ ...emptyRow(), variantId: row.variantId, adjustmentQty: '1' }))];
      })} />}
      <div className="mb-8">
        <p className="text-sm text-gray-500">
          Inventory / Stock Adjustment
        </p>

        <h1 className="mt-1 text-3xl font-bold">Stock Adjustment</h1>

        <p className="mt-2 text-gray-500">
          Record damaged, lost, found, or other stock corrections.
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-2xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-2xl bg-green-50 p-4 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-5 text-lg font-semibold">
          Adjustment Information
        </h2>

        <div className="grid gap-4 lg:grid-cols-3">
          <select
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            {reasons.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>

          <select
            value={storeId}
            onChange={(event) => changeStore(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
            disabled={loading}
          >
            <option value="">Select store or warehouse</option>

            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} · {store.type}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={occurredAt}
            onChange={(event) => setOccurredAt(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          />
        </div>

        <p className="mt-4 text-xs text-gray-500">
          {isStockIn
            ? "This adjustment adds stock to the selected location."
            : "This adjustment removes stock from the selected location."}
        </p>
      </div>

      <div className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 p-6">
          <div>
            <h2 className="text-lg font-semibold">Products</h2>
            <p className="mt-1 text-sm text-gray-500">
              All rows are submitted together as one inventory adjustment.
            </p>
          </div>

          <button
            type="button"
            disabled={!storeId}
            onClick={() => setPickerOpen(true)}
            className="flex h-10 items-center gap-2 rounded-xl bg-primary-700 px-4 text-white disabled:opacity-50"
          >
            <FiPlus />
            Find products
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Product Variant
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Current Stock
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Adjustment Qty
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Unit Cost
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  New Stock
                </th>

                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => {
                const balance = balanceByVariantId.get(row.variantId);
                const currentStock = number(balance?.quantityOnHand);
                const adjustmentQty = number(row.adjustmentQty);
                const newStock = isStockIn
                  ? currentStock + adjustmentQty
                  : currentStock - adjustmentQty;

                return (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="min-w-80 px-6 py-4">
                      <select
                        value={row.variantId}
                        disabled={!storeId}
                        onChange={(event) =>
                          updateRow(
                            index,
                            "variantId",
                            event.target.value,
                          )
                        }
                        className="h-11 w-full rounded-xl border border-gray-200 px-4"
                      >
                        <option value="">Select product variant</option>

                        {storeBalances.map((item) => (
                          <option key={item.variantId} value={item.variantId}>
                            {variantLabel(item)}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td className="px-6 py-4 font-medium text-gray-700">
                      {row.variantId ? quantity(currentStock) : "—"}
                    </td>

                    <td className="px-6 py-4">
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={row.adjustmentQty}
                        disabled={!row.variantId}
                        onChange={(event) =>
                          updateRow(
                            index,
                            "adjustmentQty",
                            event.target.value,
                          )
                        }
                        placeholder="0"
                        className="h-11 w-32 rounded-xl border border-gray-200 px-4"
                      />
                    </td>

                    <td className="px-6 py-4">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.unitCost}
                        disabled={!row.variantId}
                        onChange={(event) =>
                          updateRow(
                            index,
                            "unitCost",
                            event.target.value,
                          )
                        }
                        placeholder={
                          isStockIn
                            ? "Optional"
                            : String(balance?.averageUnitCost || "Auto")
                        }
                        className="h-11 w-32 rounded-xl border border-gray-200 px-4"
                      />
                    </td>

                    <td
                      className={`px-6 py-4 font-bold ${
                        newStock < 0 ? "text-red-600" : "text-gray-900"
                      }`}
                    >
                      {row.variantId ? quantity(newStock) : "—"}
                    </td>

                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => removeRow(index)}
                        className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {storeId && storeBalances.length === 0 && (
          <div className="border-t border-gray-100 p-5 text-sm text-gray-500">
            This store has no existing inventory balances. Use Opening Stock
            for the first stock entry of a product at this location.
          </div>
        )}
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold">Remarks</h2>

        <textarea
          rows="4"
          value={remarks}
          onChange={(event) => setRemarks(event.target.value)}
          placeholder="Explain the adjustment, e.g. damaged during stock handling..."
          className="w-full rounded-xl border border-gray-200 p-4 outline-none focus:border-primary-700"
        />
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => {
            setRows([emptyRow()]);
            setRemarks("");
            setError("");
            setSuccess("");
          }}
          className="h-11 rounded-xl border border-gray-200 px-6"
        >
          Clear Form
        </button>

        <button
          type="button"
          disabled={saving || loading}
          onClick={submitAdjustment}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-6 text-white disabled:opacity-60"
        >
          <FiCheckCircle />
          {saving ? "Submitting…" : "Submit Adjustment"}
        </button>
      </div>

      <p className="mt-4 text-center text-xs text-gray-400">
        Drafts are intentionally unavailable because the current backend stores
        only posted, immutable inventory movements.
      </p>
    </div>
  );
}
